import { Observable } from 'rxjs';
import { SupplierStatusV1 } from 'src/app/shared/enum/SupplierStatusV1';

export type VerificationType = 'bank' | 'tin' | 'iban' | 'global_tin' | 'gst' | 'eu_vat';

export type VerificationStatus = 'hidden' | 'idle' | 'pending' | 'accepted' | 'rejected' | 'error';

export interface VerificationResult {
  type: VerificationType;
  description: string;
  logicScore?: number | null;
  status?: 'accepted' | 'rejected' | null; // result status (not UI status)
  details?: any;
  raw?: any;
  errorMessage?: string | null;
  severity?: 'error' | 'warning' | null;
  panelIndex: number;
  isExpired?: boolean;
  runOn?: string | null;
}

export interface SupplierContext {
  id: string;
  country?: string | null;
  status?: SupplierStatusV1 | string | null; // keep string for safety if backend sends unknown
  approvals?: Array<{ userId: string; stage: 'Initial' | 'Final' }>;
}

export interface VerificationLabels {
  primary: string;
  secondary?: string;
}

export interface InlineRenderSpec {
  type: VerificationType;
  panelIndex: number;
  labels: VerificationLabels;
  onPrimary(): Promise<void>;
  onSecondary?: () => Promise<void>;
}

export interface VerificationPayload {
  countryCode: string;
  body: any;
  fieldLabels?: { clearingId?: string; accountNumber?: string };
}

export type SupportMatrix = Record<VerificationType, ReadonlySet<string>>;

export interface Verifier {
  readonly type: VerificationType;
  readonly labels: VerificationLabels;

  hasRequiredData(survey: any, panelIndex: number): boolean;

  buildPayload(survey: any, panelIndex: number, extra?: Record<string, any>): VerificationPayload;

  run(payload: VerificationPayload): Observable<VerificationResult> | Promise<VerificationResult>;
}

export interface VerificationEvents {
  setVerifying: (v: boolean) => void;
  openModal: (visible?: boolean) => void;
  setResult: (result: VerificationResult) => void;
  toast: (msg: string, type: 'success' | 'error' | 'warn') => void;
  registerTarget?: (type: VerificationType, panelIndex: number, el: HTMLElement) => void;
}

export interface VerificationFieldConfig {
  type: string;
  countries: string[];
  anchorField: string;
  fields: Record<string, string[]>;
}

export const BANK_SUPPORTED_CC = new Set([
  'AE',
  'AR',
  'BD',
  'BE',
  'BR',
  'CN',
  'FR',
  'GB',
  'ID',
  'IN',
  'IT',
  'KR',
  'MX',
  'MY',
  'NG',
  'NL',
  'NP',
  'PE',
  'PK',
  'PL',
  'SA',
  'TH',
  'TR',
  'UG',
  'US',
  'UY',
  'VN',
  'ZA',
]);

export function resolveField(data: Record<string, unknown>, names: string[]): unknown {
  for (const name of names) {
    const v = data[name];
    if (v != null && (typeof v !== 'string' || v.trim() !== '')) return v;
  }
  return undefined;
}

export function resolveFieldName(
  survey: { getQuestionByName: (n: string) => any },
  names: string[]
): string | undefined {
  for (const name of names) {
    if (survey.getQuestionByName(name) != null) return name;
  }
  return undefined;
}
